/**
 * Modal content for each feature card on the landing page.
 * Keyed by the feature card title (must match CMS or fallback title exactly).
 */

export interface ModalSectionData {
  title: string;
  items?: { heading: string; text: string }[];
  bullets?: string[];
  paragraphs?: string[];
}

export interface FeatureModalData {
  modalTitle: string;
  icon: string;
  gradient: string;
  intro: string[];
  sections: ModalSectionData[];
}

const featureModalContent: Record<string, FeatureModalData> = {
  'Verified Financial Capture': {
    modalTitle: 'Open Banking — Verified Data for Better Decisioning',
    icon: 'Building2',
    gradient: 'from-cyan-500 to-teal-500',
    intro: [
      'Open Banking enables organisations to access permissioned, real-time financial data directly from a customer\u2019s bank account, significantly improving the accuracy and reliability of affordability assessments.',
      'By integrating Open Banking within Debt Solutions, organisations can move beyond self-reported data and build a more complete, verified view of a customer\u2019s financial position.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Automated income and expenditure identification', text: 'Surface salary, benefits, household bills, and recurring commitments without manual input' },
          { heading: 'Reduced reliance on customer estimates', text: 'Minimise inaccuracies associated with self-reported financial information' },
          { heading: 'Faster, lower-friction journeys', text: 'Pre-populated data reduces completion time and improves engagement' },
          { heading: 'Stronger, evidence-based decisioning', text: 'Support consistent, defensible affordability outcomes using verified data' },
        ],
      },
      {
        title: 'Operational and regulatory benefits',
        bullets: [
          'Improves data quality across affordability and vulnerability assessments',
          'Supports fair, consistent, and auditable decision-making',
          'Reduces manual review and operational overhead',
          'Aligns with regulatory expectations around evidence-based outcomes',
        ],
      },
      {
        title: 'Security and data governance',
        bullets: [
          'Customer consent is required for all data access',
          'Data is read-only and securely transmitted',
          'No credentials are stored or visible',
          'Fully aligned with UK Open Banking standards and data protection requirements',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Open Banking data is automatically structured and integrated into the wider journey \u2014 feeding into affordability assessments, workflow decisions, and the central data warehouse for reporting and insight.',
        ],
      },
    ],
  },

  'Digital I&E Assessments': {
    modalTitle: 'Digital I&E Assessments \u2014 Structured, Scalable Affordability Capture',
    icon: 'FileCheck',
    gradient: 'from-teal-500 to-emerald-500',
    intro: [
      'Digital Income & Expenditure (I&E) Assessments provide a structured, consistent way to capture a customer\u2019s financial position \u2014 forming the foundation for affordability decisioning.',
      'Within Debt Solutions, I&E is delivered through adaptive, guided journeys rather than solely static forms, improving both data quality and customer completion.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Structured financial data capture', text: 'Consistent collection of income, household costs, and financial commitments' },
          { heading: 'Adaptive questioning', text: 'Journeys adjust in real time based on customer responses and context' },
          { heading: 'Improved completion rates', text: 'Guided experiences reduce drop-off compared to traditional forms' },
          { heading: 'Standardised outputs', text: 'Generate clean, comparable affordability profiles across all customers' },
        ],
      },
      {
        title: 'Operational and regulatory benefits',
        bullets: [
          'Supports consistent and auditable affordability assessments',
          'Aligns with recognised frameworks (e.g. SFS where required)',
          'Reduces manual intervention and rework',
          'Enables fair and defensible outcomes at scale',
        ],
      },
      {
        title: 'Customer experience impact',
        bullets: [
          'Simplifies complex financial disclosure',
          'Reduces the burden of lengthy, static forms',
          'Allows customers to provide information in a guided, supported way',
          'Improves engagement across a broader range of customer demographics',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Digital I&E Assessments sit at the core of Debt Solutions, enhanced by Open Banking, document capture, and AI-guided support (Penny).',
          'All data is structured in real time and fed into the wider platform \u2014 supporting decisioning, workflow progression, and downstream insight within the data warehouse.',
        ],
      },
    ],
  },

  'Intelligent Workflow Engine': {
    modalTitle: 'Intelligent Workflow Engine \u2014 Orchestrating Customer Journeys',
    icon: 'BrainCircuit',
    gradient: 'from-purple-500 to-indigo-500',
    intro: [
      'The Intelligent Workflow Engine enables organisations to design, deploy, and manage end-to-end customer journeys without code \u2014 from initial contact through to outcome delivery.',
      'Workflows combine data collection nodes, decision points, integrations, and actions into a single, auditable journey that adapts to each customer\u2019s circumstances.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Visual journey builder', text: 'Drag-and-drop workflow designer with conditional branching, loops, and parallel paths' },
          { heading: 'Dynamic decision nodes', text: 'Route customers based on affordability outcomes, vulnerability flags, or custom business rules' },
          { heading: 'Multi-channel orchestration', text: 'Trigger emails, SMS, agent tasks, or third-party integrations at any point in the journey' },
          { heading: 'Version control and audit trail', text: 'Track every change to workflow logic with full version history' },
        ],
      },
      {
        title: 'Operational benefits',
        bullets: [
          'Reduce reliance on manual case handling and ad-hoc processes',
          'Ensure consistent treatment across all customer journeys',
          'Rapidly iterate on journey design without developer involvement',
          'Full auditability of every decision and action taken',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'The workflow engine sits at the heart of the platform, orchestrating Open Banking connections, I&E capture, Penny AI conversations, vulnerability screening, and outcome delivery into a single, cohesive experience.',
        ],
      },
    ],
  },

  'Penny AI Conversations': {
    modalTitle: 'Penny AI \u2014 Intelligent, Empathetic Customer Engagement',
    icon: 'MessageCircle',
    gradient: 'from-purple-500 to-cyan-500',
    intro: [
      'Penny is an AI-powered conversational assistant designed to guide customers through sensitive financial conversations with empathy, accuracy, and compliance.',
      'By replacing rigid forms with adaptive, human-like dialogue, Penny improves data quality, increases completion rates, and ensures customers feel heard throughout the process.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Human-like guided conversations', text: 'Natural, empathetic dialogue that builds trust and encourages honest, complete responses' },
          { heading: 'Adaptive questioning', text: 'Penny adjusts her questions based on previous answers, skipping irrelevant sections and probing where needed' },
          { heading: 'Real-time data extraction', text: 'Income, expenditure, and personal details are captured and validated during the conversation' },
          { heading: 'Vulnerability detection', text: 'Penny identifies signs of vulnerability and adjusts tone, pace, and signposting accordingly' },
        ],
      },
      {
        title: 'Operational and regulatory benefits',
        bullets: [
          'Higher completion rates compared to traditional form-based journeys',
          'Consistent, compliant data collection across all customer interactions',
          'Reduced need for follow-up calls to gather missing information',
          'Full conversation transcripts available for audit and quality assurance',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Penny operates within the workflow engine, collecting structured data that feeds directly into affordability assessments, Open Banking connections, and outcome decisions \u2014 all within a single, seamless customer journey.',
        ],
      },
    ],
  },

  'Vulnerability Detection': {
    modalTitle: 'Vulnerability Detection \u2014 Identifying and Supporting At-Risk Customers',
    icon: 'Shield',
    gradient: 'from-amber-500 to-orange-500',
    intro: [
      'Vulnerability detection enables organisations to identify customers who may be in vulnerable circumstances \u2014 ensuring they receive appropriate support, signposting, and treatment throughout their journey.',
      'The platform uses a combination of conversational signals, financial data patterns, and configurable rules to flag potential vulnerability early in the process.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Automated vulnerability screening', text: 'Detect indicators of vulnerability through conversation analysis, financial patterns, and self-reported data' },
          { heading: 'Configurable vulnerability categories', text: 'Define and weight vulnerability indicators aligned to FCA guidance (health, life events, resilience, capability)' },
          { heading: 'Adaptive journey responses', text: 'Automatically adjust tone, pacing, signposting, and escalation paths when vulnerability is detected' },
          { heading: 'Case flagging and escalation', text: 'Flag vulnerable cases for human review with full context and supporting evidence' },
        ],
      },
      {
        title: 'Regulatory alignment',
        bullets: [
          'Supports FCA Consumer Duty obligations around vulnerable customer treatment',
          'Provides auditable evidence of vulnerability identification and response',
          'Ensures consistent screening across all customer journeys',
          'Configurable to align with organisational vulnerability policies',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Vulnerability signals are captured during Penny AI conversations and I&E assessments, fed into workflow decision nodes, and recorded in the data warehouse for reporting, oversight, and continuous improvement.',
        ],
      },
    ],
  },

  'Centralised Data Warehouse': {
    modalTitle: 'Centralised Data Warehouse \u2014 Unified Reporting and Insight',
    icon: 'Database',
    gradient: 'from-blue-500 to-indigo-500',
    intro: [
      'The centralised data warehouse aggregates all customer journey data \u2014 from initial contact through to outcome \u2014 into a single, structured repository for reporting, analysis, and insight.',
      'Every data point captured across Open Banking, I&E assessments, Penny AI conversations, and workflow decisions is stored, linked, and available for downstream use.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'End-to-end journey visibility', text: 'Track every customer interaction, data point, and decision from start to finish' },
          { heading: 'Operational reporting', text: 'Monitor completion rates, journey times, outcome distributions, and agent performance' },
          { heading: 'Regulatory reporting', text: 'Generate audit-ready reports on affordability outcomes, vulnerability treatment, and decision consistency' },
          { heading: 'Data-driven optimisation', text: 'Identify bottlenecks, drop-off points, and opportunities to improve journey design' },
        ],
      },
      {
        title: 'Data governance and security',
        bullets: [
          'Role-based access controls ensure data is only visible to authorised users',
          'Full audit trail of data access and modifications',
          'Data retention policies configurable per organisation',
          'Compliant with GDPR and UK data protection requirements',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'The data warehouse sits beneath the entire platform, receiving structured data from every node in the workflow engine. It powers dashboards, exports, and API integrations \u2014 providing a single source of truth across all customer journeys.',
        ],
      },
    ],
  },

  'Open Banking Integration': {
    modalTitle: 'Open Banking Integration \u2014 Real-Time Verified Financial Data',
    icon: 'Link2',
    gradient: 'from-cyan-500 to-teal-500',
    intro: [
      'Open Banking integration provides secure, permissioned access to real-time bank transaction data \u2014 enabling accurate income verification, expenditure categorisation, and affordability assessment.',
      'By connecting directly to a customer\u2019s bank account, organisations can replace self-reported estimates with verified financial data.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Automated income verification', text: 'Identify salary, benefits, and other income sources directly from bank transactions' },
          { heading: 'Expenditure categorisation', text: 'Automatically categorise spending into housing, utilities, transport, and other SFS-aligned categories' },
          { heading: 'Instant bank statement analysis', text: 'Process months of transaction data in seconds, surfacing patterns and anomalies' },
          { heading: 'Pre-populated I&E forms', text: 'Reduce manual data entry by pre-filling income and expenditure fields from verified data' },
        ],
      },
      {
        title: 'Security and compliance',
        bullets: [
          'Fully FCA-regulated Open Banking connection',
          'Customer-initiated consent required for every connection',
          'Read-only access \u2014 no ability to move funds',
          'Data encrypted in transit and at rest',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Open Banking data flows directly into I&E assessments, affordability calculations, and workflow decision nodes \u2014 providing verified evidence for every outcome decision.',
        ],
      },
    ],
  },

  'Adaptive Customer Journeys': {
    modalTitle: 'Adaptive Customer Journeys \u2014 Intelligent, Real-Time Data Capture',
    icon: 'TrendingUp',
    gradient: 'from-indigo-500 to-purple-500',
    intro: [
      'Adaptive Customer Journeys replace static, one-size-fits-all forms with dynamic experiences that adjust in real time to each customer\u2019s situation.',
      'Within Debt Solutions, journeys respond to inputs, verified data, and context \u2014 ensuring that only relevant questions are asked, while still capturing a complete and structured financial picture.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Real-time journey adaptation', text: 'Questioning adjusts based on customer responses, financial context, and verified data sources' },
          { heading: 'Smarter data capture', text: 'Focus on relevant information while maintaining completeness and consistency' },
          { heading: 'Reduced customer friction', text: 'Shorter, more intuitive journeys improve engagement and completion rates' },
          { heading: 'Consistent, structured outputs', text: 'Complex financial situations are translated into standardised, decision-ready data' },
        ],
      },
      {
        title: 'Data and insight layer',
        paragraphs: [
          'Every interaction is captured and structured within the platform\u2019s central data environment, enabling:',
        ],
        bullets: [
          'Segmentation of customers based on financial behaviour and affordability',
          'Identification of patterns, trends, and risk indicators',
          'Continuous refinement of journeys based on real-world performance',
          'A clear, auditable record of how outcomes were reached',
        ],
      },
      {
        title: 'Operational and analytical benefits',
        bullets: [
          'Improves data quality across all customer segments',
          'Enables consistent treatment of both simple and complex cases',
          'Reduces manual intervention and exception handling',
          'Supports scalable, repeatable processes across large volumes',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Adaptive journeys are powered by Paylink and enhanced by Open Banking, Digital I&E, and other data sources.',
          'As customers progress through the journey, data is structured in real time \u2014 feeding both immediate decisioning and longer-term insight within the data warehouse.',
        ],
      },
    ],
  },

  'FCA Consumer Duty Ready': {
    modalTitle: 'FCA Consumer Duty Ready \u2014 Built for Fair, Evidence-Based Outcomes',
    icon: 'Shield',
    gradient: 'from-emerald-500 to-green-500',
    intro: [
      'Debt Solutions is designed to support organisations in meeting the expectations of FCA Consumer Duty \u2014 embedding fair treatment, transparency, and evidence-based decisioning throughout the customer journey.',
      'Rather than relying on manual processes or inconsistent data, the platform enables a structured, auditable approach to affordability and customer outcomes.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Consistent customer treatment', text: 'Standardised journeys reduce variability and ensure fair, repeatable outcomes' },
          { heading: 'Evidence-based decisioning', text: 'Decisions are supported by verified, structured financial data' },
          { heading: 'Clear customer understanding', text: 'Guided journeys help ensure customers provide accurate and complete information' },
          { heading: 'Outcome-focused processes', text: 'Journeys are designed to move customers toward appropriate and sustainable solutions' },
        ],
      },
      {
        title: 'Operational and regulatory benefits',
        bullets: [
          'Supports compliance with Consumer Duty principles, including fair value and good outcomes',
          'Provides clear audit trails for decision-making and customer interactions',
          'Reduces reliance on subjective or manual assessments',
          'Enables scalable, compliant processes across large customer populations',
        ],
      },
      {
        title: 'Monitoring and oversight',
        paragraphs: [
          'The platform captures and structures customer data throughout the journey, allowing organisations to:',
        ],
        bullets: [
          'Monitor outcomes across different customer segments',
          'Identify potential areas of customer harm or friction',
          'Demonstrate consistency and fairness in treatment',
          'Support internal governance and regulatory reporting',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Consumer Duty alignment is embedded across all components of Debt Solutions \u2014 from adaptive journeys and AI-guided interactions (Penny), to verified data inputs and structured outputs within the data warehouse.',
          'This ensures that compliance is not an overlay, but built into how decisions are made and outcomes are delivered.',
        ],
      },
    ],
  },

  'Automated Decisioning': {
    modalTitle: 'Automated Decisioning \u2014 Consistent, Data-Driven Outcomes at Scale',
    icon: 'BrainCircuit',
    gradient: 'from-violet-500 to-purple-500',
    intro: [
      'Automated Decisioning enables organisations to assess affordability and determine appropriate next steps in real time \u2014 using structured, verified data captured throughout the customer journey.',
      'Within Debt Solutions, decisioning is embedded directly into the process, reducing reliance on manual review while ensuring outcomes remain consistent, explainable, and aligned with policy.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Real-time affordability decisions', text: 'Assess customer position instantly based on structured financial data' },
          { heading: 'Policy-driven consistency', text: 'Apply decision frameworks consistently across all customers and scenarios' },
          { heading: 'Reduced manual intervention', text: 'Minimise operational overhead and exception handling' },
          { heading: 'Outcome routing', text: 'Direct customers toward the most appropriate next step or solution' },
        ],
      },
      {
        title: 'Operational and regulatory benefits',
        bullets: [
          'Ensures consistent application of affordability and risk policies',
          'Supports fair, explainable, and auditable decision-making',
          'Reduces processing time and cost per case',
          'Enables scalable decisioning across high customer volumes',
        ],
      },
      {
        title: 'Transparency and control',
        bullets: [
          'Decision logic is structured and traceable',
          'Outcomes can be reviewed, audited, and refined over time',
          'Supports internal governance and regulatory oversight',
          'Allows for controlled human intervention where required',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Automated Decisioning sits at the core of Debt Solutions, powered by data captured through adaptive journeys and enhanced by inputs such as Open Banking and Digital I&E.',
          'All decisions are informed by structured data, an approved knowledge base, and continuously supported by insight from the data warehouse \u2014 enabling both immediate outcomes and ongoing optimisation.',
        ],
      },
    ],
  },

  'Enterprise Security': {
    modalTitle: 'Enterprise Security \u2014 Built for Regulated Environments',
    icon: 'Lock',
    gradient: 'from-slate-500 to-zinc-600',
    intro: [
      'Debt Solutions is designed to meet the security, privacy, and governance requirements of regulated organisations \u2014 ensuring customer data is protected at every stage of the journey.',
      'The platform combines secure architecture, strict access controls, and industry-aligned standards to support enterprise-grade deployment.',
    ],
    sections: [
      {
        title: 'What this enables',
        items: [
          { heading: 'Secure data handling', text: 'Financial data is encrypted in transit and at rest using industry-standard protocols' },
          { heading: 'Controlled access', text: 'Role-based permissions ensure only authorised users can access relevant data' },
          { heading: 'Data segregation', text: 'Logical separation ensures client data remains isolated and protected' },
          { heading: 'Resilient infrastructure', text: 'Built to support high availability and secure operation at scale' },
        ],
      },
      {
        title: 'Compliance and standards',
        bullets: [
          'GDPR aligned data processing and storage',
          'ISO 27001 certified infrastructure',
          'Bank-grade encryption standards',
          'Regular security testing, including independent penetration testing',
        ],
      },
      {
        title: 'Operational and regulatory benefits',
        bullets: [
          'Supports internal InfoSec and procurement requirements',
          'Reduces risk associated with handling sensitive financial data',
          'Enables secure scaling across multiple clients and use cases',
          'Provides confidence for both organisations and their customers',
        ],
      },
      {
        title: 'How it fits within the platform',
        paragraphs: [
          'Security is embedded across every layer of Debt Solutions \u2014 from customer interaction and data capture (including Open Banking), through to processing, storage, and reporting within the data warehouse.',
          'This ensures that data protection and compliance are maintained throughout the full lifecycle of every customer journey.',
        ],
      },
    ],
  },
};

export default featureModalContent;
